import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { isOverdue } from './MilestoneTracker';

const { FiEdit2, FiTrash2, FiCalendar } = FiIcons;

const COLUMNS = [
  { id: 'todo',        label: 'To Do',      color: 'text-zinc-400',   bar: 'bg-zinc-600',     border: 'border-zinc-700/40' },
  { id: 'in_progress', label: 'In Progress', color: 'text-brand-pink', bar: 'bg-brand-pink',   border: 'border-brand-pink/20' },
  { id: 'review',      label: 'Review',      color: 'text-amber-400',  bar: 'bg-amber-400',    border: 'border-amber-500/20' },
  { id: 'done',        label: 'Done',        color: 'text-green-400',  bar: 'bg-green-500',    border: 'border-green-500/20' },
];

const PRIORITY_DOT = { high: 'bg-red-400', medium: 'bg-amber-400', low: 'bg-zinc-500' };

const KanbanCard = ({ milestone: m, canManage, onEdit, onDelete, onToggle }) => {
  const overdue = isOverdue(m);
  const done = m.completed || m.status === 'done';

  return (
    <div className={`p-3 rounded-2xl border backdrop-blur-sm transition-all group ${
      done ? 'border-white/5 bg-white/[0.02]'
      : overdue ? 'border-red-500/25 bg-red-500/5'
      : 'border-white/8 bg-white/[0.04] hover:bg-white/[0.07] hover:border-white/15'
    }`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className={`text-xs font-black leading-tight ${done ? 'line-through text-zinc-600' : overdue ? 'text-red-300' : 'text-white'}`}>
          {m.title}
        </p>
        {canManage && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            <button onClick={() => onEdit(m)} className="p-1 rounded-lg text-zinc-600 hover:text-brand-pink hover:bg-brand-pink/10 transition-all">
              <SafeIcon icon={FiEdit2} className="text-[10px]" />
            </button>
            <button onClick={() => onDelete(m.id)} className="p-1 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all">
              <SafeIcon icon={FiTrash2} className="text-[10px]" />
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${PRIORITY_DOT[m.priority] || PRIORITY_DOT.medium}`} />
        {m.category && m.category !== 'other' && (
          <span className="text-[9px] font-black uppercase tracking-widest text-brand-coral/70">{m.category}</span>
        )}
        {(m.tags || []).slice(0, 2).map(tag => (
          <span key={tag} className="px-1 py-0.5 rounded bg-white/5 text-[8px] font-black text-zinc-600">#{tag}</span>
        ))}
      </div>

      {m.status === 'in_progress' && m.progress_pct > 0 && (
        <div className="mb-2">
          <div className="h-1 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-brand-pink to-brand-coral"
              style={{ width: `${m.progress_pct}%` }} />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        {m.target_date && (
          <span className={`flex items-center gap-1 text-[9px] font-black uppercase ${overdue && !done ? 'text-red-400' : 'text-zinc-600'}`}>
            <SafeIcon icon={FiCalendar} className="text-[8px]" />
            {new Date(m.target_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}
        {m.assignee_label && (
          <span className="text-[9px] font-black text-zinc-600 truncate max-w-[80px]">👤 {m.assignee_label}</span>
        )}
      </div>

      {canManage && !done && (
        <button onClick={() => onToggle(m.id, true)}
          className="mt-2 w-full py-1.5 rounded-xl border border-green-500/20 text-green-400 text-[9px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 hover:bg-green-500/10 transition-all">
          Mark Done
        </button>
      )}
    </div>
  );
};

const MilestoneKanban = ({ milestones, canManage, onToggle, onEdit, onDelete, onUpdateMilestone }) => {
  const byStatus = (statusId) => milestones.filter(m => {
    if (statusId === 'done') return m.completed || m.status === 'done';
    return (m.status || 'todo') === statusId && !m.completed;
  });

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {COLUMNS.map(col => {
        const items = byStatus(col.id);
        return (
          <div key={col.id} className={`rounded-2xl border ${col.border} bg-white/[0.02] p-3 space-y-3`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${col.bar}`} />
                <span className={`text-[10px] font-black uppercase tracking-widest ${col.color}`}>{col.label}</span>
              </div>
              <span className="text-[10px] font-black text-zinc-600">{items.length}</span>
            </div>
            {items.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-[9px] font-black uppercase tracking-widest text-zinc-800">Empty</p>
              </div>
            ) : (
              items.map(m => (
                <KanbanCard key={m.id} milestone={m} canManage={canManage}
                  onToggle={onToggle} onEdit={onEdit} onDelete={onDelete} />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MilestoneKanban;